import { CAUSE } from "./deviceCheckRules"
import { permissionDeviceName, permissionPathFor, systemPermissionPath } from "./browserPermissionPaths"
import { audienceCopy, browserLabelInSentence } from "./deviceCheckCopy"

/**
 * Les échelles de dépannage.
 *
 * Un barreau = un seul essai demandé à l'élève, suivi d'une re-vérification.
 * On ne descend au suivant que si le test échoue encore. Quatre règles :
 *  - un essai à la fois ;
 *  - trois barreaux au plus une fois l'accès accordé : un élève de dix ans ne
 *    suit pas une liste plus longue, et les essais rares (volume d'entrée du
 *    système, pilote coincé) ne se règlent pas seul devant l'écran ;
 *  - les barreaux sans objet sont retirés : pas de « change de micro » quand
 *    il n'y en a qu'un ;
 *  - ça se termine : au bout, l'écran dit d'entrer quand même.
 *
 * Les textes s'adressent à un enfant : une phrase par geste, le verbe en
 * premier, pas de « périphérique », « autorisation » ni « navigateur » quand un
 * mot courant existe. `label` est la formulation courte envoyée à l'équipe
 * technique dans « Déjà essayé ».
 */

const isDenied = (cause) => cause === CAUSE.DENIED || cause === CAUSE.BLOCKED_BY_SYSTEM

/**
 * Où rendre l'accès : le chemin de chaque navigateur (`browserPermissionPaths`, la seule source, que le
 * live lit aussi), sans jamais nommer un côté de l'écran — les navigateurs déplacent leurs boutons ;
 * on nomme l'icône, et c'est la détection automatique qui fait avancer l'écran dès que l'accès est donné.
 */
const permissionTip = (device) => ({ isWeb, browser }) =>
    isWeb
        ? [
            ...permissionPathFor(browser, device),
            /*
             * Pas de bouton « Recharger » à nous : un élève qui ne lit pas
             * cliquerait dessus à l'aveugle, et il ne ferait rien. Le
             * navigateur propose lui-même de recharger quand l'accès change, et
             * l'écran re-vérifie l'accès toutes les cinq secondes.
             */
            {
                icon: "sync-alt",
                text: `Si ${browserLabelInSentence(browser)} te propose de recharger la page, clique dessus. Sinon, attends un peu : je vérifie tout seul.`,
            },
        ]
        : [
            { icon: "cog", text: `Ouvre les Réglages de ton téléphone, puis Masteur, et allume ${permissionDeviceName(device)}.` },
            { icon: "arrow-left", text: "Reviens ici : je revérifie tout seul." },
        ]

export const MICROPHONE_LADDER = [
    {
        id: "permission",
        label: "autorisation",
        applies: ({ cause }) => isDenied(cause),
        tip: (context) =>
            context.cause === CAUSE.BLOCKED_BY_SYSTEM
                ? systemPermissionPath("microphone")
                : permissionTip("microphone")(context),
    },
    {
        id: "no-device",
        label: "aucun micro branché",
        applies: ({ cause }) => cause === CAUSE.NO_DEVICE,
        tip: () => [
            { icon: "headphones", text: "Branche un casque ou des écouteurs avec un micro." },
            { icon: "comment", text: "Puis dis « allô »." },
        ],
    },
    {
        id: "hardware-mute",
        label: "casque muet",
        applies: ({ cause }) => cause === CAUSE.GRANTED,
        tip: () => [
            { icon: "headphones", text: "Ton casque a un petit bouton qui le rend muet : vérifie qu'il n'est pas activé." },
            { icon: "keyboard", text: "Regarde aussi la touche micro barrée de ton clavier." },
            { icon: "comment", text: "Puis dis « allô »." },
        ],
    },
    {
        id: "wrong-device",
        label: "changement de micro",
        // N'a de sens que s'il y a réellement le choix.
        applies: ({ cause, deviceCount }) => cause === CAUSE.GRANTED && deviceCount > 1,
        tip: () => "Essaie l'autre micro dans la liste : c'est peut-être le mauvais qui écoute.",
        picker: "audioinput",
    },
    {
        id: "other-app",
        label: "autre appli",
        applies: ({ cause }) => cause === CAUSE.GRANTED || cause === CAUSE.IN_USE,
        tip: () => [
            { icon: "window-close", text: "Une autre appli utilise peut-être ton micro (Zoom, Teams, Meet). Ferme-la." },
            { icon: "comment", text: "Puis dis « allô »." },
        ],
    },
]

export const CAMERA_LADDER = [
    {
        id: "app-toggle",
        label: "vidéo coupée dans l'app",
        applies: ({ isVideoMuted }) => isVideoMuted === true,
        tip: ({ audience }) => audienceCopy(audience).videoOff,
    },
    {
        id: "permission",
        label: "autorisation",
        applies: ({ cause }) => cause === CAUSE.DENIED,
        tip: permissionTip("camera"),
    },
    {
        id: "system-privacy",
        label: "confidentialité du système",
        // Ce blocage n'existe que sur ordinateur : sur mobile il n'y a qu'un
        // seul endroit à vérifier, déjà couvert par le barreau autorisation.
        applies: ({ cause, isWeb }) => isWeb && cause === CAUSE.BLOCKED_BY_SYSTEM,
        tip: () => systemPermissionPath("camera"),
    },
    {
        id: "other-app",
        label: "autre appli",
        applies: ({ cause }) => cause === CAUSE.IN_USE,
        tip: () => [
            { icon: "window-close", text: "Une autre appli utilise peut-être ta caméra (Zoom, Teams, Meet). Ferme-la." },
            { icon: "eye", text: "Puis regarde à nouveau l'image." },
        ],
    },
    {
        id: "no-device",
        label: "aucune caméra",
        applies: ({ cause }) => cause === CAUSE.NO_DEVICE,
        tip: () => "Je ne trouve pas de caméra. Si tu as une webcam, branche-la. Sinon, tu peux entrer sans vidéo.",
    },
    {
        id: "obstructed",
        label: "objectif masqué",
        // Sur web, l'analyse d'image a déjà tranché : ce barreau ne sort que
        // sur une image effectivement noire — ou sur mobile, où rien n'analyse.
        applies: ({ cause, isWeb }) =>
            cause === CAUSE.DARK_IMAGE || (!isWeb && cause === CAUSE.GRANTED),
        tip: ({ isWeb }) =>
            isWeb
                ? [
                    { icon: "arrows-alt-h", text: "Beaucoup d'ordinateurs ont un petit cache qui glisse devant la caméra : ouvre-le." },
                    { icon: "hand-paper", text: "Enlève l'autocollant, s'il y en a un." },
                ]
                : [
                    { icon: "hand-paper", text: "Vérifie qu'un doigt ou la coque ne cache pas la caméra." },
                    { icon: "lightbulb", text: "Mets-toi dans un endroit plus éclairé." },
                ],
    },
    {
        id: "framing",
        label: "cadrage et lumière",
        // Le cas que l'analyse ne sait pas voir : l'image est nette, mais
        // l'élève ne s'y trouve pas. Pas pendant une vidéo coupée : il n'y a
        // rien à cadrer.
        applies: ({ cause, isWeb, isVideoMuted }) =>
            isWeb && cause === CAUSE.GRANTED && !isVideoMuted,
        tip: () => [
            { icon: "lightbulb", text: "Mets-toi face à une fenêtre ou une lampe, pas dos à elle." },
            { icon: "eye", text: "Recule un peu : on doit voir tout ton visage." },
        ],
    },
    {
        id: "wrong-device",
        label: "changement de caméra",
        // Vaut aussi pour une image noire : basculer sur l'autre caméra règle
        // souvent tout. Pas pendant une vidéo coupée.
        applies: ({ cause, deviceCount, isVideoMuted }) =>
            (cause === CAUSE.GRANTED || cause === CAUSE.DARK_IMAGE) &&
            deviceCount > 1 &&
            !isVideoMuted,
        tip: () => "Essaie l'autre caméra dans la liste : c'est peut-être la mauvaise qui est allumée.",
        picker: "videoinput",
    },
]

export const SPEAKER_LADDER = [
    {
        id: "volume",
        label: "volume de sortie",
        applies: () => true,
        tip: () => [
            { icon: "volume-up", text: "Monte le son de ton ordinateur (ou de ta tablette)." },
            { icon: "volume-mute", text: "Regarde l'icône du son, en bas ou en haut de l'écran : elle ne doit pas être barrée." },
        ],
    },
    {
        id: "headphones",
        label: "casque",
        applies: () => true,
        tip: () => [
            { icon: "headphones", text: "Tu as un casque ? Branche-le. Il est déjà branché ? Débranche-le." },
            { icon: "redo", text: "Puis réécoute." },
        ],
    },
    {
        id: "other-app",
        label: "autre appli",
        applies: () => true,
        tip: () => [
            { icon: "window-close", text: "Une autre appli joue peut-être du son (une vidéo, un jeu). Ferme-la." },
            { icon: "redo", text: "Puis réécoute." },
        ],
    },
]

export const NETWORK_LADDER = [
    {
        id: "closer-to-box",
        label: "rapprochement de la box",
        applies: () => true,
        tip: () => [
            { icon: "wifi", text: "Rapproche-toi de la box Wi-Fi." },
            { icon: "door-open", text: "Ou change de pièce." },
        ],
    },
    {
        id: "drop-video",
        label: "passage en audio seul",
        applies: () => true,
        tip: () => [
            { icon: "video-slash", text: "Coupe ta vidéo : ta voix passera beaucoup mieux." },
            { icon: "video", text: "Tu pourras la rallumer plus tard." },
        ],
    },
    {
        id: "other-network",
        label: "autre réseau",
        applies: () => true,
        tip: ({ audience }) => [
            { icon: "mobile-alt", text: audienceCopy(audience).otherNetwork },
            { icon: "ethernet", text: "Ou branche un câble entre l'ordinateur et la box, si elle est à côté." },
        ],
    },
]

/** Ne garde que les barreaux qui ont un sens dans la situation constatée. */
export function relevantRungs(ladder, context) {
    return ladder.filter((rung) => rung.applies(context))
}
